import React, { useContext, useState, useEffect } from 'react';
import { Sparkles, X } from 'lucide-react';
import { BusinessAutomationActionTypeEnum } from '@reach/shared-types/business-automation';
import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import { SendMessageForm } from './SendMessageForm';
import { AIEmailGeneratorDialog } from './AIEmailGeneratorDialog';
import { StripoDialog } from './stripo';
import styles from './SendMessageStep.module.css';
import { usePartnerProfile } from '@/hooks/usePartnerSettings';
import type { SendMessageFormField } from './types';
import type { GeneratePartnerEmailResponse } from './useGeneratePartnerEmail';

type SendMessageAction = Extract<
  AutomationTemplateContents['actionData'][0],
  { type: BusinessAutomationActionTypeEnum.SEND_COMMUNICATION }
>;

interface SendMessageDialogProps {
  open: boolean;
  onClose: () => void;
  action: SendMessageAction;
  actionIndex: number;
}

export const SendMessageDialog: React.FC<SendMessageDialogProps> = ({
  open,
  onClose,
  action,
  actionIndex,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateAction, updateUsePointOfContactSender } = editor;
  const { partner } = usePartnerProfile();

  const [formData, setFormData] = useState({
    subject: action.subject || '',
    previewText: action.previewText || '',
    html: action.html || '',
    css: action.css || '',
    compiledHtml: action.compiledHtml || '',
    text: action.text || '',
    sendToPOC: !!editor.state.localContents.usePointOfContactSender,
  });

  const effectiveHtml = formData.html || partner?.emailTemplateOverride?.html || '';
  const effectiveCss = formData.css || partner?.emailTemplateOverride?.css || '';
  const [stripoIsOpen, setShowStripo] = useState(false);
  const [aiDialogIsOpen, setAiDialogIsOpen] = useState(false);

  // Reset form data when dialog opens or action changes
  useEffect(() => {
    if (open) {
      setFormData({
        subject: action.subject || '',
        previewText: action.previewText || '',
        html: action.html || '',
        css: action.css || '',
        compiledHtml: action.compiledHtml || '',
        text: action.text || '',
        sendToPOC: !!editor.state.localContents.usePointOfContactSender,
      });
    }
  }, [open, action, editor.state.localContents.usePointOfContactSender]);

  if (!open) return null;

  const handleOK = () => {
    const { sendToPOC, ...actionData } = formData;

    updateAction(actionIndex, {
      subject: actionData.subject,
      previewText: actionData.previewText,
      html: actionData.html,
      css: actionData.css,
      compiledHtml: actionData.compiledHtml,
      text: actionData.text,
    });
    updateUsePointOfContactSender(sendToPOC);
    onClose();
  };

  const setFormFieldValue = (field: SendMessageFormField, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const showStripo = () => setShowStripo(true);
  const hideStripo = () => setShowStripo(false);
  const openAI = () => {
    const hasExistingEmail = !!formData.html?.trim();
    if (hasExistingEmail) {
      const shouldReplace = window.confirm(
        'This will replace your existing email template, subject line, and preview text. Are you sure?',
      );
      if (!shouldReplace) return;
    }
    setAiDialogIsOpen(true);
  };
  const closeAI = () => setAiDialogIsOpen(false);

  const handleStripoSave = (newHtml: string, newCss: string, compiledHtml: string) => {
    setFormData(prev => ({
      ...prev,
      html: newHtml,
      css: newCss,
      compiledHtml,
    }));
    setShowStripo(false);
  };

  const handleAIGenerated = (generated: GeneratePartnerEmailResponse) => {
    setFormData(prev => ({
      ...prev,
      html: generated.html,
      css: generated.css,
      compiledHtml: generated.compiledHtml,
      subject: generated.subject,
      previewText: generated.previewText,
      text: generated.smsText,
    }));
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header className={styles.dialogHeader}>
          <div className={styles.dialogHeaderCopy}>
            <h2>Configure Send Message</h2>
            <p>Compose your message content</p>
          </div>
          <div className={styles.dialogHeaderActions}>
            <button type="button" className={styles.aiButton} onClick={openAI}>
              <Sparkles />
              Build with AI
            </button>
            <button type="button" className={styles.closeButton} onClick={onClose}>
              <X />
            </button>
          </div>
        </header>

        <main>
          <SendMessageForm
            subject={formData.subject}
            previewText={formData.previewText}
            html={effectiveHtml}
            css={effectiveCss}
            text={formData.text}
            onChange={setFormFieldValue}
            onOpenStripoEditor={showStripo}
            sendToPointOfContact={formData.sendToPOC}
          />
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>

      <StripoDialog
        open={stripoIsOpen}
        onClose={hideStripo}
        html={effectiveHtml}
        css={effectiveCss}
        onSave={handleStripoSave}
      />
      <AIEmailGeneratorDialog open={aiDialogIsOpen} onClose={closeAI} onGenerated={handleAIGenerated} />
    </>
  );
};
